import { getTheme, TooltipDelay, TooltipHost } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPIconButton, IPSpinner, IPText } from 'baseComponents';
import { IPList } from 'baseComponents/List/IPList';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import ScrollableStackItem from 'components/Utils/ScrollableStackItem';
import { globalTextStylesPaddingSmallBold, newIcon } from 'globalStyles';
import toInteger from 'lodash/toInteger';
import Assignee from 'models/assignee';
import Task, { TaskStateId, TaskTypes } from 'models/tasks/task';
import { TaskState } from 'models/tasks/taskHelperClasses';
import { SystemTaskTypes } from 'models/tasks/taskType';
import User from 'models/user';
import { FunctionComponent, useContext, useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import AppError from 'utils/appError';
import { ControllerExceptionCodes } from 'utils/controllerException';
import { newGuidNil } from 'utils/guid';
import { sortOnDate, sortOnString } from 'utils/sorting';
import { ICardTaskProps } from './Cards/KanbanCard';
import KanbanCardEmptyLane from './Cards/KanbanCardEmptyLane';
import KanbanCardEventGeneric from './Cards/KanbanCardEventGeneric';
import KanbanCardTaskGeneric from './Cards/KanbanCardTaskGeneric';
import KanbanCardTaskGenericWithState from './Cards/KanbanCardTaskGenericWithState';
import { getKanbanSortOrder, IKanbanSortProps, updateTaskKanban } from './KanbanBoardSortHelper';

//
// List
//
interface IKanbanTaskListProps {
  title: string;
  index: number;
  tasks: Task[];
  onTaskClick: (task: Task) => void;
  onNewTask?: (listIndex: number, taskList: Task[]) => Promise<void>;
  taskTemplate?: FunctionComponent<ICardTaskProps>;
  allowNew: boolean;
  isCompleted: boolean;
  statusField: 'state' | 'assignee';
}

const KanbanTaskList = (props: IKanbanTaskListProps) => {
  const theme = getTheme();
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation']);

  const getTemplate = (task: Task): JSX.Element => {
    if (props.taskTemplate) {
      return <props.taskTemplate key={task.taskId.toString()} task={task} onClick={props.onTaskClick} />;
    } else {
      if (task.taskType === TaskTypes.Event && task.isSeries()) {
        return <KanbanCardEventGeneric key={task.taskId.toString()} task={task} onClick={props.onTaskClick} />;
      } else {
        if (props.statusField === 'state') {
          return <KanbanCardTaskGeneric key={task.taskId.toString()} task={task} onClick={props.onTaskClick} />;
        } else {
          return (
            <KanbanCardTaskGenericWithState key={task.taskId.toString()} task={task} onClick={props.onTaskClick} />
          );
        }
      }
    }
  };

  const onRenderCard = (task?: Task, index?: number | undefined) => {
    if (!task || index === undefined) return null;
    let dragDisabled = false;

    //prevent dragging of approval tasks
    //status: approval tasks are moved to completed when approved or rejected
    //user: approval tasks cannot be re-assigned
    //the task's own system type decides: the cache holds the current heads only, so an approval
    //pinned to an older version of the approval form would not be recognised by its version id
    if (task.systemTaskType === SystemTaskTypes.Approval) {
      dragDisabled = true;
    }

    return (
      <Draggable key={task.taskId} draggableId={task.taskId.toString()} index={index} isDragDisabled={dragDisabled}>
        {(provided, snapshot) => {
          return (
            <div
              ref={provided.innerRef}
              {...provided.draggableProps}
              {...provided.dragHandleProps}
              style={{
                userSelect: 'none',
                margin: '5px',
                minHeight: '50px',
                backgroundColor: snapshot.isDragging
                  ? appContext.useDarkMode
                    ? theme.palette.themeDark
                    : theme.palette.whiteTranslucent40
                  : appContext.useDarkMode
                  ? theme.palette.blueDark
                  : theme.palette.white,
                borderRadius: '3px',
                border: '1px solid lightgrey',
                ...provided.draggableProps.style,
              }}
            >
              <TooltipHost
                delay={TooltipDelay.zero}
                content={dragDisabled ? t('translation:General.Notifications.NotDraggable') : undefined}
              >
                {getTemplate(task)}
              </TooltipHost>
            </div>
          );
        }}
      </Draggable>
    );
  };

  //
  // Main render
  //
  return (
    <IPStack verticalFill styles={{ root: { minWidth: 220 } }}>
      <IPStack horizontal horizontalAlign="space-between" verticalAlign="center">
        <IPStack.Item>
          <IPText variant="mediumPlus" styles={globalTextStylesPaddingSmallBold}>
            {props.title}
          </IPText>
        </IPStack.Item>
        <IPStack.Item>
          {props.allowNew && props.onNewTask && (
            <IPIconButton onClick={() => props.onNewTask!(props.index, props.tasks)} iconProps={newIcon} />
          )}
        </IPStack.Item>
      </IPStack>

      <Droppable droppableId={props.index.toString()} key={props.index}>
        {(provided, snapshot) => {
          return (
            <IPStack
              verticalFill
              styles={{
                root: {
                  width: '210px',
                  height: '100%',
                  padding: '5px',
                  background: snapshot.isDraggingOver
                    ? appContext.useDarkMode
                      ? theme.palette.black
                      : theme.palette.themeLight
                    : appContext.useDarkMode
                    ? theme.palette.blackTranslucent40
                    : theme.palette.themeLighterAlt,
                },
              }}
            >
              <ScrollableStackItem>
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {props.tasks.length === 0 && <KanbanCardEmptyLane />}
                  <IPList items={props.tasks} onRenderCell={onRenderCard} />
                  {provided.placeholder}
                </div>
              </ScrollableStackItem>
            </IPStack>
          );
        }}
      </Droppable>
    </IPStack>
  );
};

//
// Board
//
interface IKanbanBoardTasksProps {
  taskStates?: TaskState[];
  statusField: 'state' | 'assignee';
  tasks: Task[];
  allowColumnReorder: boolean;
  isLoading: boolean;
  onTaskClick: (task: Task) => void;
  onNewTask?: (statusIndex: number, taskList: Task[], statusObj: TaskState | Assignee) => Promise<void>;
  taskTemplate?: FunctionComponent<ICardTaskProps>;
  allowNew: boolean;
}

const KanbanBoardTasks = (props: IKanbanBoardTasksProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation', 'exceptions']);
  const [taskStates, setTaskStates] = useState<TaskState[]>([]);
  const [assignees, setAssignees] = useState<Assignee[]>([]);
  const [lists, setLists] = useState<Task[][]>([]);
  const [isInitializing, setIsInitializing] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  useEffect(() => {
    if (!isInitialized) {
      initTasks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    initTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.tasks, props.statusField]);

  const getUniqueAssignees = (tasks: Task[]): Assignee[] => {
    //get all the unique assignees (users and groups) from the task list
    const assigneeList: Assignee[] = [];
    for (const task of tasks) {
      let assignee: Assignee | undefined;
      if (task.user && task.userId) {
        assignee = new Assignee(task.userId, task.user.name, task.user.email, 'user');
      }
      if (
        assignee &&
        assignee.id !== newGuidNil() && //system user
        assigneeList.findIndex((a) => {
          return assignee && assignee.id === a.id;
        }) === -1
      ) {
        assigneeList.push(assignee);
      }
    }

    return assigneeList.sort((a, b) => sortOnString(a.name, b.name));
  };

  const initTasks = () => {
    if (isInitializing) {
      return;
    }

    try {
      setIsInitializing(true);

      const newLists: Task[][] = [];

      switch (props.statusField) {
        case 'state':
          if (!props.taskStates) return;
          const statusesTaskState = props.taskStates
            //.filter((a) => a.taskStateId !== TaskStateId.Archived)
            .sort((a, b) => a.sortOrder - b.sortOrder);
          statusesTaskState.forEach((status: TaskState) => {
            const statusList: Task[] = props.tasks.filter((task: Task) => task.taskStateId === status.taskStateId);
            statusList.sort((a, b) => {
              const result = a.sortOrder - b.sortOrder;
              if (result !== 0) {
                return result;
              } else {
                return sortOnDate(a.created, b.created);
              }
            });

            newLists.push(statusList);
          });

          setTaskStates(statusesTaskState);
          break;

        case 'assignee':
          const statusesAssignees = getUniqueAssignees(props.tasks);
          statusesAssignees.forEach((status: Assignee) => {
            const statusList: Task[] = props.tasks.filter((task: Task) => task.userId === status.id);
            statusList.sort((a, b) => {
              return a.sortOrder - b.sortOrder;
            });

            newLists.push(statusList);
          });

          setAssignees(statusesAssignees);
          break;
      }

      setLists(newLists);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsInitializing(false);
      setIsInitialized(true);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updateTask = (listIndex: any, itemData: any, fromIndex: any, toIndex: any) => {
    const lists1 = lists.slice();
    lists1[listIndex] = reorder(lists1[listIndex], itemData, fromIndex, toIndex);
    setLists(lists1);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onTaskDrop = async (listIndexFrom: any, itemData: any, fromIndex: any, listIndexTo: any, toIndex: any) => {
    let prevTask: Task | undefined = undefined;

    try {
      if (
        listIndexFrom === undefined ||
        itemData === undefined ||
        fromIndex === undefined ||
        listIndexTo === undefined ||
        toIndex === undefined
      ) {
        return;
      }

      if (listIndexFrom === listIndexTo && fromIndex === toIndex) {
        return;
      }

      //prevent dropping to state Archived directly
      //only allow from state Completed to Archived
      if (props.statusField === 'state') {
        const toState = taskStates[listIndexTo];
        if (toState.taskStateId === TaskStateId.Archived) {
          const fromState = taskStates[listIndexFrom];
          if (fromState.taskStateId !== TaskStateId.Completed) {
            appContext.showNotification(t('tasks:TaskView.Board.NoDirectArchive'), false, true);

            return;
          }
        }
      }

      const task: Task = itemData;
      prevTask = task.clone();

      // update the task status
      switch (props.statusField) {
        case 'state':
          task.taskStateId = taskStates[listIndexTo].taskStateId;
          break;
        case 'assignee':
          const assignee = assignees[listIndexTo];
          if (assignee.type === 'user') {
            task.userId = assignee.id;
            task.user = new User(assignee.id, assignee.email, assignee.name);
          }
          break;
      }

      // update sort order
      const sortProps: IKanbanSortProps<Task> = {
        appContext: appContext,
        itemList: lists[listIndexTo],
        sourceIndex: listIndexFrom === listIndexTo ? fromIndex : -1,
        targetIndex: toIndex,
        updateItem: updateTaskKanban,
      };

      task.sortOrder = await getKanbanSortOrder(sortProps);

      // update the lists
      updateTask(listIndexFrom, itemData, fromIndex, -1);
      updateTask(listIndexTo, itemData, -1, toIndex);

      // update the database async
      updateTaskKanban(appContext, task, props.statusField).catch((err) => {
        // revert the change
        if (prevTask) {
          itemData.taskStateId = prevTask.taskStateId;
          itemData.userId = prevTask.userId;
          itemData.user = prevTask.user;
          itemData.sortOrder = prevTask.sortOrder;
          updateTask(listIndexFrom, itemData, -1, fromIndex);
          if (listIndexFrom !== listIndexTo) {
            updateTask(listIndexTo, itemData, toIndex, -1);
          }
        }
        if (AppError.getControllerExceptionCode(err) === ControllerExceptionCodes.Code_General_NotAuthorized) {
          appContext.showNotification(t('exceptions:Task.NoAuth'), true);
        } else {
          appContext.setError(err);
        }
      });
    } catch (err) {
      appContext.setError(err);
    } finally {
      initTasks();
    }
  };

  const onTaskClick = (task: Task) => {
    props.onTaskClick(task);
  };

  const onNewTask = async (listIndex: number, taskList: Task[]) => {
    if (!props.onNewTask || !props.allowNew) return;
    let obj: TaskState | Assignee;
    switch (props.statusField) {
      case 'state':
        obj = taskStates[listIndex];
        break;
      case 'assignee':
        obj = assignees[listIndex];
        break;
    }

    await props.onNewTask(listIndex, taskList, obj);
  };

  const getList = (tasks: Task[], listIndex: number) => {
    let listTitle: string;
    switch (props.statusField) {
      case 'state':
        if (listIndex >= taskStates.length) {
          return null;
        }
        listTitle = taskStates[listIndex].state;
        break;
      case 'assignee':
        if (listIndex >= assignees.length) {
          return null;
        }
        listTitle = assignees[listIndex].name;
        break;
    }

    return (
      <KanbanTaskList
        key={listTitle}
        title={listTitle}
        index={listIndex}
        tasks={tasks}
        statusField={props.statusField}
        onTaskClick={onTaskClick}
        taskTemplate={props.taskTemplate}
        onNewTask={onNewTask}
        allowNew={props.allowNew}
        isCompleted={props.statusField === 'state' ? taskStates[listIndex].completed : false}
      ></KanbanTaskList>
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const reorder = (items: any[], item: any, fromIndex: any, toIndex: any) => {
    if (fromIndex >= 0) {
      items = [...items.slice(0, fromIndex), ...items.slice(fromIndex + 1)];
    }

    if (toIndex >= 0) {
      items = [...items.slice(0, toIndex), item, ...items.slice(toIndex)];
    }

    return items;
  };

  //
  // Main render
  //
  if (props.isLoading || isInitializing) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  } else {
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', height: '100%', overflowX: 'scroll' }}>
        <DragDropContext
          onDragEnd={(result) =>
            onTaskDrop(
              toInteger(result.source?.droppableId),
              lists[toInteger(result.source.droppableId)][result.source.index],
              result.source.index,
              toInteger(result.destination?.droppableId),
              result.destination?.index,
            )
          }
        >
          <IPStack horizontal>
            {lists.map((tasks: Task[], listIndex: number) => {
              return <IPStack.Item key={listIndex}>{getList(tasks, listIndex)}</IPStack.Item>;
            })}
          </IPStack>
        </DragDropContext>
      </div>
    );
  }
};

export default KanbanBoardTasks;
